import React, {
  useMemo,
  createContext,
  ReactNode,
  useContext,
  useEffect,
} from "react";
import {QueryVariables} from "@reside/ui";

import {
  SlideNode,
  UIState,
  UIActions,
  Draft,
  DraftActions,
  CompletionCheck,
} from "./model";
import {useGeneratePdf} from "../inline-pdf-viewer/hooks/useGeneratePdf";
import {getPdfDocumentName} from "./sign-admission/helpers";
import {useQuery} from "react-query";
import {ApiResponse} from "apisauce";
import {http} from "../../services/api/restApiClient";

type Props = Readonly<{
  draft: Draft;
  draftActions: DraftActions;
  uiState: UIState;
  uiActions: UIActions;
  completionChecklist: CompletionCheck[];
  logoutSession: () => void;
  loadingPatchAdmission: boolean;
  hasClearHiddenFieldsFlag: boolean;
  children: ReactNode;
}> &
  QueryVariables;

export type AdmissionContextType = Omit<Props, "children"> &
  Readonly<{
    currentSlide: SlideNode;
    // PDF generation state
    pdfFile: File | null;
    isPdfGenerating: boolean;
    hasScrollEnforcementFeatureFlag: boolean;
  }>;

export const AdmissionContext = createContext<AdmissionContextType>(undefined);

export const AdmissionContextConsumer = ({
  children,
}: {
  children: (context: AdmissionContextType) => ReactNode;
}) => (
  <AdmissionContext.Consumer>
    {context => {
      if (context === undefined) {
        throw new Error(
          "AdmissionContextConsumer must be used within the AdmissionContextProvider",
        );
      }

      return children(context);
    }}
  </AdmissionContext.Consumer>
);

export const AdmissionContextProvider = (props: Props) => {
  const {
    draft,
    draftActions,
    uiState,
    uiActions,
    completionChecklist,
    logoutSession,
    loadingPatchAdmission,
    children,
    queryVariables,
    hasClearHiddenFieldsFlag,
  } = props;

  const isLastSlide = draft.slides[uiState.slideIdx]?.state.last;
  const isResidentExperience = Boolean(uiState.slideIdx); // uiState has slideIdx only in resident experience

  const {data: hasScrollEnforcementFeatureFlag} = useQuery<{
    hasFeature: boolean;
  }>({
    queryKey: ["hasScrollEnforcementFeatureFlag"],
    queryFn: async () => {
      const response: ApiResponse<{hasFeature: boolean}> = await http.post(
        "/workflows/facility-has-feature",
        {
          featureType: "ENFORCE_PDF_SCROLL_TO_CONFIRM",
        },
      );
      return response.data;
    },
    enabled: isResidentExperience,
    staleTime: Infinity, // We don't want to refetch this query on every slide change
  });

  const {
    file: pdfFile,
    isGenerating: isPdfGenerating,
    hasFailed: hasPdfGenerationFailed,
    generatePdf,
  } = useGeneratePdf({
    workflowId: draft.instance.id,
    pdfDocumentName: getPdfDocumentName(draft),
    hasScrollEnforcementFeatureFlag:
      hasScrollEnforcementFeatureFlag?.hasFeature ?? false,
  });

  // Start PDF generation when the last slide is reached and the feature flag is enabled. This ensures that the PDF is generated before the user tries to sign the admission.
  // A failed attempt is not retried: it leaves no file and nothing generating, so without the
  // hasPdfGenerationFailed check this effect would start a new generation after every failure.
  useEffect(() => {
    if (
      isLastSlide &&
      !pdfFile &&
      !isPdfGenerating &&
      !hasPdfGenerationFailed &&
      hasScrollEnforcementFeatureFlag?.hasFeature
    ) {
      generatePdf();
    }
  }, [
    isLastSlide,
    pdfFile,
    isPdfGenerating,
    hasPdfGenerationFailed,
    generatePdf,
    hasScrollEnforcementFeatureFlag,
    isResidentExperience,
  ]);

  const value = useMemo(() => {
    return {
      currentSlide: draft.slides[uiState.slideIdx],
      draft,
      draftActions,
      uiActions,
      uiState,
      completionChecklist,
      logoutSession,
      loadingPatchAdmission,
      queryVariables,
      hasClearHiddenFieldsFlag,
      // PDF state
      pdfFile,
      isPdfGenerating,
      hasScrollEnforcementFeatureFlag:
        hasScrollEnforcementFeatureFlag?.hasFeature ?? false,
    };
  }, [
    draft,
    draftActions,
    uiState,
    uiActions,
    completionChecklist,
    logoutSession,
    loadingPatchAdmission,
    queryVariables,
    hasClearHiddenFieldsFlag,
    pdfFile,
    isPdfGenerating,
    hasScrollEnforcementFeatureFlag,
  ]);

  return (
    <AdmissionContext.Provider value={value}>
      {children}
    </AdmissionContext.Provider>
  );
};

export const useAdmissionContext = () => useContext(AdmissionContext);
